import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Avatar, Box, Button, Chip, IconButton, Menu, MenuItem, Paper, Stack, Typography,
} from '@mui/material';
import CallIcon from '@mui/icons-material/Call';
import EmailIcon from '@mui/icons-material/Email';
import EventIcon from '@mui/icons-material/Event';
import NotesIcon from '@mui/icons-material/Notes';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import type { ActionItem, ActionType } from '../../api/types';
import { useCompleteAction, useDeleteAction } from '../../api/hooks';
import { formatDateTime, parseUtc } from '../common/format';
import { ConfirmDialog, EmptyState, UserText } from '../common/Common';
import { CompleteActionDialog } from './CompleteActionDialog';

const ICONS: Record<ActionType, typeof CallIcon> = {
  call: CallIcon,
  email: EmailIcon,
  meeting: EventIcon,
  note: NotesIcon,
  task: TaskAltIcon,
};

export function ActionTimeline({
  actions, onEdit, showOpportunity = false,
}: {
  actions: ActionItem[];
  onEdit: (action: ActionItem) => void;
  showOpportunity?: boolean;
}) {
  const { t } = useTranslation();
  const complete = useCompleteAction();
  const remove = useDeleteAction();
  const [menuFor, setMenuFor] = useState<ActionItem | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [completing, setCompleting] = useState<ActionItem | null>(null);
  const [deleting, setDeleting] = useState<ActionItem | null>(null);

  if (actions.length === 0) {
    return <EmptyState title={t('timeline.empty')} description={t('timeline.emptyDescription')} />;
  }

  const now = Date.now();

  return (
    <Stack spacing={1.5} data-testid="timeline">
      {actions.map((action) => {
        const Icon = ICONS[action.type];
        const done = Boolean(action.completedAtUtc);
        const overdue = !done && action.dueAtUtc != null && parseUtc(action.dueAtUtc).getTime() < now;

        return (
          <Paper
            key={action.id}
            variant="outlined"
            sx={{ p: 2, borderRadius: '10px', opacity: done ? 0.75 : 1 }}
            data-testid={`action-${action.id}`}
          >
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
              <Avatar sx={{ width: 32, height: 32, bgcolor: done ? 'success.main' : 'secondary.main' }}>
                {done ? <CheckCircleIcon fontSize="small" /> : <Icon fontSize="small" />}
              </Avatar>

              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Typography
                    variant="h4"
                    sx={{ textDecoration: done ? 'line-through' : 'none' }}
                  >
                    <UserText>{action.subject}</UserText>
                  </Typography>
                  <Chip size="small" label={t(`actionType.${action.type}`)} variant="outlined" />
                  {overdue && (
                    <Chip size="small" label={t('timeline.overdue')} color="error" data-testid={`action-overdue-${action.id}`} />
                  )}
                </Stack>

                {action.notes && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    <UserText multiline>{action.notes}</UserText>
                  </Typography>
                )}

                {action.outcome && (
                  <Typography variant="body2" sx={{ mt: 0.75 }}>
                    <Typography component="span" variant="overline" color="text.secondary" sx={{ mr: 1 }}>
                      {t('timeline.outcome')}
                    </Typography>
                    <UserText>{action.outcome}</UserText>
                  </Typography>
                )}

                {/* The activity date leads, because that is what the entry is filed under. The
                    creation date is not shown at all — it was only ever noise. */}
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mt: 1 }}
                  data-testid={`action-date-${action.id}`}
                >
                  {formatDateTime(action.activityAtUtc)} · {action.createdByName}
                  {done && ` · ${t('timeline.completed', { date: formatDateTime(action.completedAtUtc) })}`}
                  {!done && action.dueAtUtc && ` · ${t('timeline.due', { date: formatDateTime(action.dueAtUtc) })}`}
                  {showOpportunity && action.opportunityId && ` · ${t('timeline.linkedToOpportunity')}`}
                </Typography>

                {!done && (
                  <Button
                    size="small"
                    sx={{ mt: 1 }}
                    onClick={() => setCompleting(action)}
                    data-testid={`complete-action-${action.id}`}
                  >
                    {t('timeline.markComplete')}
                  </Button>
                )}
              </Box>

              <IconButton
                size="small"
                aria-label={t('timeline.options')}
                onClick={(e) => {
                  setMenuFor(action);
                  setMenuAnchor(e.currentTarget);
                }}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Paper>
        );
      })}

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
        <MenuItem
          onClick={() => {
            setMenuAnchor(null);
            if (menuFor) onEdit(menuFor);
          }}
        >
          {t('common.edit')}
        </MenuItem>
        <MenuItem
          onClick={() => {
            setMenuAnchor(null);
            setDeleting(menuFor);
          }}
        >
          {t('common.delete')}
        </MenuItem>
      </Menu>

      <CompleteActionDialog
        action={completing}
        onClose={() => setCompleting(null)}
        onConfirm={(outcome) => {
          if (completing) complete.mutate({ id: completing.id, outcome });
          setCompleting(null);
        }}
      />

      <ConfirmDialog
        open={Boolean(deleting)}
        title={t('timeline.deleteTitle')}
        message={t('timeline.deleteMessage')}
        busy={remove.isPending}
        onClose={() => setDeleting(null)}
        onConfirm={() => {
          if (deleting) remove.mutate(deleting.id);
          setDeleting(null);
        }}
      />
    </Stack>
  );
}
